import _ from 'lodash'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

import { CreateExpertiseDto, Expertise, UpdateExpertiseDto } from '@/core/models/expertise.model'
import { WithId } from '@/core/types/shared'

import { Tag, baseApi } from './base'
import { fetchPresignedUrl, uploadToAWS } from './file'

type DiagnoseExpertiseParams = {
  conclusion: string
  diagnostic: string
}

function manualUpdateCachedExpertiseAction(expertiseId: string, updatedExpertise: Expertise) {
  return expertiseApi.util.updateQueryData('getExpertise', expertiseId, (cachedExpertise) => {
    Object.assign(cachedExpertise, updatedExpertise)
  })
}

export const expertiseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getExpertise: builder.query<Expertise, string>({
      query: (expertiseId) => `/expertises/${expertiseId}`,
      providesTags: (_result, _error, expertiseId) => [{ type: Tag.EXPERTISE, id: expertiseId }],
    }),
    createExpertise: builder.mutation<Expertise, CreateExpertiseDto>({
      query: (body) => ({
        url: '/expertises',
        method: 'POST',
        body,
      }),
    }),
    updateExpertise: builder.mutation<Expertise, WithId<UpdateExpertiseDto>>({
      query: ({ id: expertiseId, ...body }) => ({
        url: `/expertises/${expertiseId}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ id: expertiseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedExpertise } = await queryFulfilled
          dispatch(manualUpdateCachedExpertiseAction(expertiseId, updatedExpertise))
        } catch {}
      },
    }),
    diagnoseExpertise: builder.mutation<Expertise, WithId<DiagnoseExpertiseParams>>({
      query: ({ id: expertiseId, ...body }) => ({
        url: `/expertises/${expertiseId}/diagnose`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ id: expertiseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedExpertise } = await queryFulfilled
          dispatch(manualUpdateCachedExpertiseAction(expertiseId, updatedExpertise))
        } catch {}
      },
    }),
    deleteExpertise: builder.mutation<Expertise, string>({
      query: (expertiseId) => ({
        url: `/expertises/${expertiseId}`,
        method: 'DELETE',
      }),
    }),
    draftExpertise: builder.mutation<Expertise, WithId<UpdateExpertiseDto>>({
      query: ({ id: expertiseId, ...body }) => ({
        url: `/expertises/${expertiseId}/draft`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ id: expertiseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedExpertise } = await queryFulfilled
          dispatch(manualUpdateCachedExpertiseAction(expertiseId, updatedExpertise))
        } catch {}
      },
    }),
    sendExpertise: builder.mutation<Expertise, WithId<{ receiverId: string; receiverType: string }>>({
      query: ({ id: expertiseId, ...body }) => ({
        url: `/expertises/${expertiseId}/send`,
        method: 'PATCH',
        body,
      }),
    }),
    createExpertiseDictaphone: builder.mutation<Expertise, { expertiseId: string; file: File }>({
      async queryFn({ expertiseId, file }, _queryApi, _extraOptions, baseQuery) {
        const presignedUrlResult = await fetchPresignedUrl(
          {
            fileName: file.name,
            fileType: file.type,
            objectType: 'expertise',
            objectId: expertiseId,
          },
          baseQuery
        )
        if (presignedUrlResult.error) return { error: presignedUrlResult.error }

        const { url } = presignedUrlResult.data as { url: string }
        const uploadResult = await uploadToAWS(url, file)
        if (uploadResult.error) return { error: uploadResult.error }

        const createDictaphoneResult = await baseQuery({
          url: `/expertises/${expertiseId}/dictaphones`,
          method: 'POST',
          body: {
            file: {
              filename: file.name,
              key: _.deburr(decodeURIComponent(new URL(url).pathname.slice(1))),
              objectType: 'expertise',
            },
          },
        })

        return createDictaphoneResult.data ? { data: createDictaphoneResult.data as Expertise } : { error: createDictaphoneResult.error as FetchBaseQueryError }
      },
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.EXPERTISE, id: arg.expertiseId }],
    }),
    deleteExpertiseDictaphone: builder.mutation<Expertise, { expertiseId: string; filename: string; key: string }>({
      query: ({ expertiseId, ...body }) => ({
        url: `/expertises/${expertiseId}/dictaphones/delete`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.EXPERTISE, id: arg.expertiseId }],
    }),
  }),
  overrideExisting: false,
})
